import { useState } from 'react';
import { FlaskConical, Sparkles, Loader2, Tags, Scale, FileDown } from 'lucide-react';
import { useConfigStore } from '@/store/useConfigStore';
import { optimizeTitle, callLLMWithImages } from '@/services/llmService';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Select, Switch } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ImageUploader } from '@/components/ui/ImageUploader';
import { DEFAULT_SKU_CONFIG, EXCEL_FIELD_LABELS } from '@/lib/constants';
import type { LLMInstance } from '@/types';

// sku 规格可优化的字段（复选框选项）
const SKU_SPEC_FIELDS = ['skuSpec1', 'skuSpec2'];

export function SkuConfigPage() {
    const { skuConfig, updateSkuConfig, llmInstances } = useConfigStore();

    const [specTestOpen, setSpecTestOpen] = useState(false);
    const [weightTestOpen, setWeightTestOpen] = useState(false);

    const specInstance = llmInstances.find(i => i.id === skuConfig.specLlmInstanceId) ?? null;
    const weightInstance = llmInstances.find(i => i.id === skuConfig.weightLlmInstanceId) ?? null;

    const specCanTest = !!specInstance && !!skuConfig.specModel && !!skuConfig.specPrompt.trim();
    const weightCanTest = !!weightInstance && !!skuConfig.weightModel && !!skuConfig.weightPrompt.trim();

    const handleSpecInstanceChange = (id: string) => {
        const inst = llmInstances.find(i => i.id === id);
        updateSkuConfig({ specLlmInstanceId: id, specModel: inst?.models[0] ?? '' });
    };
    const handleWeightInstanceChange = (id: string) => {
        const inst = llmInstances.find(i => i.id === id);
        updateSkuConfig({ weightLlmInstanceId: id, weightModel: inst?.models[0] ?? '' });
    };

    const toggleSpecField = (key: string) => {
        const set = new Set(skuConfig.specFields);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        updateSkuConfig({ specFields: Array.from(set) });
    };

    return (
        <div className="flex h-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div>
                    <h1 className="text-base font-semibold text-zinc-900">SKU 配置</h1>
                    <p className="text-xs text-zinc-500">SKU 规格优化与 SKU 估重，批量执行时调用 LLM 处理对应字段</p>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-4">
                <div className="mx-auto flex max-w-3xl flex-col gap-4">
                    {/* SKU 规格优化 */}
                    <section className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Tags size={16} className="text-indigo-500" />
                                <div>
                                    <h2 className="text-sm font-semibold text-zinc-900">SKU 规格优化</h2>
                                    <p className="text-xs text-zinc-500">提取 SKU 重点，清理无用字符，超长时浓缩</p>
                                </div>
                            </div>
                            <Switch
                                checked={skuConfig.specEnabled}
                                onChange={(v) => updateSkuConfig({ specEnabled: v })}
                                label={skuConfig.specEnabled ? '已启用' : '已禁用'}
                            />
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Select
                                label="LLM 实例"
                                value={skuConfig.specLlmInstanceId}
                                onChange={(e) => handleSpecInstanceChange(e.target.value)}
                                disabled={!skuConfig.specEnabled}
                            >
                                <option value="">请选择实例</option>
                                {llmInstances.map(inst => (
                                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                                ))}
                            </Select>
                            <Select
                                label="模型"
                                value={skuConfig.specModel}
                                onChange={(e) => updateSkuConfig({ specModel: e.target.value })}
                                disabled={!skuConfig.specEnabled || !specInstance}
                            >
                                <option value="">请选择模型</option>
                                {specInstance && specInstance.models.map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </Select>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Select label="提交方式" value={skuConfig.specSubmissionMode} onChange={(e) => updateSkuConfig({ specSubmissionMode: e.target.value as typeof skuConfig.specSubmissionMode })} disabled={!skuConfig.specEnabled}>
                                <option value="single">逐 SKU 请求</option><option value="json">单商品 JSON</option><option value="csv">单商品 CSV</option><option value="batch-json">多商品批量 JSON</option>
                            </Select>
                            <Input label="每批商品数" type="number" min={1} max={100} value={skuConfig.specBatchProductCount} onChange={(e) => updateSkuConfig({ specBatchProductCount: Math.max(1, Math.min(100, Number(e.target.value) || 10)) })} disabled={!skuConfig.specEnabled || skuConfig.specSubmissionMode !== 'batch-json'} />
                        </div>

                        <div className="mt-3">
                            <label className="text-xs font-medium text-zinc-600">优化字段</label>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {SKU_SPEC_FIELDS.map(key => {
                                    const checked = skuConfig.specFields.includes(key);
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => toggleSpecField(key)}
                                            disabled={!skuConfig.specEnabled}
                                            className={[
                                                'flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
                                                checked
                                                    ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                                                    : 'border-zinc-300 bg-white text-zinc-600 hover:border-zinc-400',
                                                !skuConfig.specEnabled ? 'cursor-not-allowed opacity-50' : '',
                                            ].join(' ')}
                                        >
                                            <span className={[
                                                'flex h-4 w-4 items-center justify-center rounded border',
                                                checked ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-zinc-300',
                                            ].join(' ')}>
                                                {checked && (
                                                    <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M2.5 6.5L5 9l4.5-5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </span>
                                            {EXCEL_FIELD_LABELS[key] ?? key}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between rounded-md border border-zinc-200 p-3">
                            <div><p className="text-xs font-medium text-zinc-700">跳过短规格</p><p className="text-xs text-zinc-400">不超过阈值的规格值不提交给 AI</p></div>
                            <div className="flex items-center gap-3"><Input type="number" min={0} max={255} value={skuConfig.specSkipShortChars} onChange={(e) => updateSkuConfig({ specSkipShortChars: Math.max(0, Number(e.target.value) || 0) })} disabled={!skuConfig.specEnabled || !skuConfig.specSkipShortEnabled} className="w-20" /><Switch checked={skuConfig.specSkipShortEnabled} onChange={(value) => updateSkuConfig({ specSkipShortEnabled: value })} /></div>
                        </div>

                        <div className="mt-3">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-zinc-600">请求间隔</label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={skuConfig.specRequestInterval}
                                        onChange={(e) => {
                                            const v = parseInt(e.target.value, 10);
                                            updateSkuConfig({ specRequestInterval: v >= 0 ? v : 0 });
                                        }}
                                        disabled={!skuConfig.specEnabled}
                                        className="w-20 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-400"
                                    />
                                    <span className="text-xs text-zinc-400">秒</span>
                                </div>
                                <p className="text-xs text-zinc-400">批量优化时每次请求之间的间隔时间</p>
                            </div>
                        </div>

                        <div className="mt-3">
                            <Textarea
                                label="提示词"
                                value={skuConfig.specPrompt}
                                onChange={(e) => updateSkuConfig({ specPrompt: e.target.value })}
                                placeholder={DEFAULT_SKU_CONFIG.specPrompt}
                                className="min-h-[120px]"
                                disabled={!skuConfig.specEnabled}
                            />
                            <div className="mt-1 flex items-center justify-between">
                                <p className="text-xs text-zinc-400">自定义提示词，控制 SKU 规格优化行为</p>
                                <button
                                    onClick={() => updateSkuConfig({ specPrompt: DEFAULT_SKU_CONFIG.specPrompt })}
                                    className="text-xs text-zinc-500 hover:text-zinc-700 hover:underline disabled:text-zinc-300 disabled:no-underline"
                                    disabled={!skuConfig.specEnabled}
                                >
                                    恢复默认
                                </button>
                            </div>
                        </div>

                        <div className="mt-3 flex justify-end">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSpecTestOpen(true)}
                                disabled={!specCanTest}
                            >
                                <FlaskConical size={14} />
                                测试
                            </Button>
                        </div>
                    </section>

                    {/* SKU 估重 */}
                    <section className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Scale size={16} className="text-emerald-500" />
                                <div>
                                    <h2 className="text-sm font-semibold text-zinc-900">SKU 估重</h2>
                                    <p className="text-xs text-zinc-500">根据产品内容估算每个 SKU 的重量（Kg）</p>
                                </div>
                            </div>
                            <Switch
                                checked={skuConfig.weightEnabled}
                                onChange={(v) => updateSkuConfig({ weightEnabled: v })}
                                label={skuConfig.weightEnabled ? '已启用' : '已禁用'}
                            />
                        </div>

                        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Select
                                label="LLM 实例"
                                value={skuConfig.weightLlmInstanceId}
                                onChange={(e) => handleWeightInstanceChange(e.target.value)}
                                disabled={!skuConfig.weightEnabled}
                            >
                                <option value="">请选择实例</option>
                                {llmInstances.map(inst => (
                                    <option key={inst.id} value={inst.id}>{inst.name}</option>
                                ))}
                            </Select>
                            <Select
                                label="模型"
                                value={skuConfig.weightModel}
                                onChange={(e) => updateSkuConfig({ weightModel: e.target.value })}
                                disabled={!skuConfig.weightEnabled || !weightInstance}
                            >
                                <option value="">请选择模型</option>
                                {weightInstance && weightInstance.models.map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </Select>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <Select label="提交方式" value={skuConfig.weightSubmissionMode} onChange={(e) => updateSkuConfig({ weightSubmissionMode: e.target.value as typeof skuConfig.weightSubmissionMode })} disabled={!skuConfig.weightEnabled}>
                                <option value="single">逐 SKU 请求</option><option value="json">单商品 JSON</option><option value="csv">单商品 CSV</option><option value="batch-json">多商品批量 JSON</option>
                            </Select>
                            <Input label="每批商品数" type="number" min={1} max={100} value={skuConfig.weightBatchProductCount} onChange={(e) => updateSkuConfig({ weightBatchProductCount: Math.max(1, Math.min(100, Number(e.target.value) || 10)) })} disabled={!skuConfig.weightEnabled || skuConfig.weightSubmissionMode !== 'batch-json'} />
                        </div>

                        <div className="mt-3">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-zinc-600">请求间隔</label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        value={skuConfig.weightRequestInterval}
                                        onChange={(e) => {
                                            const v = parseInt(e.target.value, 10);
                                            updateSkuConfig({ weightRequestInterval: v >= 0 ? v : 0 });
                                        }}
                                        disabled={!skuConfig.weightEnabled}
                                        className="w-20 rounded-md border border-zinc-300 px-2 py-1 text-sm text-zinc-900 disabled:bg-zinc-50 disabled:text-zinc-400"
                                    />
                                    <span className="text-xs text-zinc-400">秒</span>
                                </div>
                                <p className="text-xs text-zinc-400">批量估重时每次请求之间的间隔时间</p>
                            </div>
                        </div>

                        <div className="mt-3">
                            <Textarea
                                label="提示词"
                                value={skuConfig.weightPrompt}
                                onChange={(e) => updateSkuConfig({ weightPrompt: e.target.value })}
                                placeholder={DEFAULT_SKU_CONFIG.weightPrompt}
                                className="min-h-[100px]"
                                disabled={!skuConfig.weightEnabled}
                            />
                            <div className="mt-1 flex items-center justify-between">
                                <p className="text-xs text-zinc-400">自定义提示词，控制 SKU 估重行为</p>
                                <button
                                    onClick={() => updateSkuConfig({ weightPrompt: DEFAULT_SKU_CONFIG.weightPrompt })}
                                    className="text-xs text-zinc-500 hover:text-zinc-700 hover:underline disabled:text-zinc-300 disabled:no-underline"
                                    disabled={!skuConfig.weightEnabled}
                                >
                                    恢复默认
                                </button>
                            </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between rounded-md border border-zinc-200 p-3">
                            <div>
                                <p className="text-xs font-medium text-zinc-700">参考 SKU 图片</p>
                                <p className="text-xs text-zinc-400">开启后，估重时会将每行的 SKU 图片一并发送给模型</p>
                            </div>
                            <Switch
                                checked={skuConfig.weightUseImage}
                                onChange={(v) => updateSkuConfig({ weightUseImage: v })}
                                label={skuConfig.weightUseImage ? '已开启' : '已关闭'}
                            />
                        </div>

                        <div className="mt-3 flex items-center justify-between rounded-md border border-zinc-200 p-3">
                            <div>
                                <p className="text-xs font-medium text-zinc-700">多轮估重</p>
                                <p className="text-xs text-zinc-400">开启后，对每个 SKU 重复估重多次并取平均值作为最终结果</p>
                            </div>
                            <Switch
                                checked={skuConfig.weightMultiRound}
                                onChange={(v) => updateSkuConfig({ weightMultiRound: v })}
                                label={skuConfig.weightMultiRound ? '已开启' : '已关闭'}
                            />
                        </div>

                        <div className="mt-3 flex items-center justify-between rounded-md border border-zinc-200 p-3">
                            <div>
                                <p className="text-xs font-medium text-zinc-700">重复估重次数</p>
                                <p className="text-xs text-zinc-400">多轮估重时生效，建议 2~5 次</p>
                            </div>
                            <input
                                type="number"
                                min={1}
                                max={10}
                                value={skuConfig.weightRepeatCount}
                                disabled={!skuConfig.weightMultiRound}
                                onChange={(e) => updateSkuConfig({ weightRepeatCount: Math.max(1, Math.min(10, parseInt(e.target.value || '1', 10) || 1)) })}
                                className="w-20 rounded-md border border-zinc-300 px-2 py-1 text-xs text-zinc-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-zinc-100 disabled:text-zinc-400"
                            />
                        </div>

                        <div className="mt-3 flex justify-end">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setWeightTestOpen(true)}
                                disabled={!weightCanTest}
                            >
                                <FlaskConical size={14} />
                                测试
                            </Button>
                        </div>
                    </section>

                    {/* 导出设置 */}
                    <section className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center gap-2">
                            <FileDown size={16} className="text-zinc-500" />
                            <div>
                                <h2 className="text-sm font-semibold text-zinc-900">导出设置</h2>
                                <p className="text-xs text-zinc-500">控制导出 Excel 时 SKU 字段的处理方式</p>
                            </div>
                        </div>

                        <div className="mt-3 flex items-center justify-between rounded-md border border-zinc-200 p-3">
                            <div>
                                <p className="text-xs font-medium text-zinc-700">覆盖平台规格</p>
                                <p className="text-xs text-zinc-400">开启后，导出时会将「SKU规格1 + SKU规格2」（用加号拼接，仅当 SKU规格2 存在）覆盖到「平台SKU」字段</p>
                            </div>
                            <Switch
                                checked={skuConfig.overwritePlatformSpec}
                                onChange={(v) => updateSkuConfig({ overwritePlatformSpec: v })}
                                label={skuConfig.overwritePlatformSpec ? '已开启' : '已关闭'}
                            />
                        </div>
                    </section>
                </div>
            </div>

            <TestModal
                open={specTestOpen}
                onClose={() => setSpecTestOpen(false)}
                title="SKU 规格优化测试"
                instance={specInstance}
                model={skuConfig.specModel}
                prompt={skuConfig.specPrompt}
                placeholder="输入要测试的 SKU 规格内容"
                inputLabel="SKU 规格内容"
            />
            <TestModal
                open={weightTestOpen}
                onClose={() => setWeightTestOpen(false)}
                title="SKU 估重测试"
                instance={weightInstance}
                model={skuConfig.weightModel}
                prompt={skuConfig.weightPrompt}
                placeholder="可输入产品信息（名称、规格等）进行估重测试"
                inputLabel="产品信息"
                allowImage={skuConfig.weightUseImage}
                submitLabel="估重"
            />
        </div>
    );
}

function TestModal({
    open,
    onClose,
    title,
    instance,
    model,
    prompt,
    placeholder,
    inputLabel,
    allowImage,
    submitLabel = '优化',
}: {
    open: boolean;
    onClose: () => void;
    title: string;
    instance: LLMInstance | null;
    model: string;
    prompt: string;
    placeholder: string;
    inputLabel: string;
    allowImage?: boolean;
    submitLabel?: string;
}) {
    const [input, setInput] = useState('');
    const [images, setImages] = useState<string[]>([]);
    const [result, setResult] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canSubmit = !!instance && !!model && (!!input.trim() || images.length > 0);

    const handleOptimize = async () => {
        if (!instance || !model || (!input.trim() && images.length === 0)) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const text = input.trim() || '请根据图片估算重量';
            const out = images.length > 0
                ? await callLLMWithImages(instance, model, prompt, text, images)
                : await optimizeTitle(instance, model, prompt, text);
            setResult(out);
        } catch (e) {
            setError(e instanceof Error ? e.message : '请求失败');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setInput('');
        setImages([]);
        setResult(null);
        setError(null);
        setLoading(false);
        onClose();
    };

    const showResult = loading || result !== null || error !== null;

    return (
        <Modal open={open} onClose={handleClose} title={title} width="lg">
            <div className="flex flex-col gap-4">
                {allowImage && (
                    <div>
                        <label className="mb-1 block text-xs font-medium text-zinc-600">参考图片</label>
                        <ImageUploader images={images} onChange={setImages} label="上传图片" max={4} />
                    </div>
                )}
                <Textarea
                    label={inputLabel}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={placeholder}
                    className="min-h-[100px]"
                />
                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleOptimize}
                        disabled={loading || !canSubmit}
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        {submitLabel}
                    </Button>
                </div>

                {showResult && (
                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                            <p className="mb-2 text-xs font-medium text-zinc-500">{submitLabel}前</p>
                            <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">
                                {input || '—'}
                            </p>
                        </div>
                        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                            <p className="mb-2 text-xs font-medium text-zinc-500">{submitLabel}后</p>
                            {loading ? (
                                <div className="flex items-center gap-2 text-sm text-zinc-400">
                                    <Loader2 size={14} className="animate-spin" />
                                    处理中...
                                </div>
                            ) : error ? (
                                <p className="whitespace-pre-wrap break-words text-sm text-red-500">{error}</p>
                            ) : (
                                <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">
                                    {result ?? '—'}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {result && (
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setResult(null);
                                setError(null);
                            }}
                        >
                            清除结果
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
}
